import { TableHead, cn } from '@repo/ui'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'

export type SortDirection = 'asc' | 'desc'

export type SortState = {
  key: string
  direction: SortDirection
} | null

type SortableTableHeadProps = {
  children: React.ReactNode
  sortKey: string
  sort: SortState
  onSort: (key: string) => void
  className?: string
}

export function SortableTableHead({
  children,
  sortKey,
  sort,
  onSort,
  className,
}: SortableTableHeadProps) {
  const isActive = sort?.key === sortKey

  return (
    <TableHead
      className={cn('cursor-pointer select-none', className)}
      onClick={() => onSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {children}
        {isActive ? (
          sort.direction === 'asc' ? (
            <ArrowUp className="size-3.5" />
          ) : (
            <ArrowDown className="size-3.5" />
          )
        ) : (
          <ArrowUpDown className="text-muted-foreground/50 size-3.5" />
        )}
      </div>
    </TableHead>
  )
}

export function toggleSort(current: SortState, key: string): SortState {
  if (current?.key === key) {
    return current.direction === 'asc' ? { key, direction: 'desc' } : null
  }
  return { key, direction: 'asc' }
}

export function sortItems<T>(
  items: Array<T>,
  sort: SortState,
  accessor: (item: T, key: string) => string | number | null | undefined,
): Array<T> {
  if (!sort) return items

  return [...items].sort((a, b) => {
    const aVal = accessor(a, sort.key)
    const bVal = accessor(b, sort.key)

    if (aVal == null && bVal == null) return 0
    if (aVal == null) return 1
    if (bVal == null) return -1

    const cmp =
      typeof aVal === 'number' && typeof bVal === 'number'
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal))

    return sort.direction === 'asc' ? cmp : -cmp
  })
}
